// @flow
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Divider, Grid, Hidden, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';

import {colors} from '../../../../assets/styles/colors';
import Collapsible from '../../../../elements/Collapsible/Collapsible';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {getImageUrl} from '../../../../utils/image_provider';
import {commaFormat} from '../../../../utils/number';
import {ORDER_STATE_STRINGS, ORDER_STATE_COLORS} from '../../../../entities';
import type {Order} from '../../../../entities';
import {useStoreCurrency} from '../../../currency/utils';
type Props = {
  order: Order,
};

const useStyles = accentColor =>
  makeStyles(theme => ({
    wrapHeader: {
      display: 'flex',
      flexDirection: 'column',
    },
    heading: {
      margin: 0,
      color: colors.grey900,
      fontSize: '14px',
      fontWeight: 500,
    },
    subHeading: {
      color: colors.grey700,
      fontSize: '14px',
    },
    wrapState: {
      display: 'flex',
      alignItems: 'center',
    },
    statePill: {
      marginLeft: theme.spacing(1),
      fontSize: '12px',
      fontWeight: 500,
      color: '#FFFFFF',
      backgroundColor: accentColor && accentColor.main,
      borderRadius: '12px',
      padding: '2px 10px',
      textAlign: 'center',
    },
    innerItem: {
      display: 'flex',
    },
    innerItemImage: {
      height: 90,
      width: 90,
      borderRadius: '12px',
      marginRight: theme.spacing(1),
    },
    itemComment: {
      fontSize: '0.75rem',
      color: colors.grey600,
      marginTop: '8px',
      marginBottom: '-8px',
    },
    innerContentText: {
      color: colors.grey800,
      fontSize: '0.875rem',
      margin: '2px 0px',
    },
    textVariant: {
      color: colors.grey600,
      fontWeight: 500,
      fontSize: '12px',
    },
    totalsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    labelDscto: {
      color: colors.success,
      fontSize: '0.875rem',
      margin: '2px 0px',
    },
    chipDscto: {
      background: '#E04763',
      fontSize: '0.875rem',
      borderRadius: 24,
      color: 'white',
      padding: '0 8px',
      marginRight: 8,
    },
    divider: {
      background: colors.grey200,
      width: '100%',
      margin: theme.spacing(2, 0, 2, 0),
    },
    bold: {
      fontWeight: 600,
    },
    quantity: {
      [theme.breakpoints.up('md')]: {
        textAlign: 'right',
      },
    },
  }));

export default function CollapseItem(props: Props) {
  const {order} = props;
  const accentColor = ORDER_STATE_COLORS[order && order.orderState];
  const stateString = ORDER_STATE_STRINGS[order && order.orderState];
  const currency = useStoreCurrency();

  const classes = useStyles(accentColor)();

  const parsedDate = useMemo(
    () =>
      new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(order.createdAt),
    [order.createdAt]
  );

  const orderStatePill = useMemo(
    () => <div className={classes.statePill}>{stateString}</div>,
    [classes.statePill, stateString]
  );

  const orderItems = useMemo(() => {
    return (
      <>
        {order &&
          order.items.map((item, i) => (
            <div key={`order-${i}`}>
              <div className={classes.innerItem}>
                <img
                  className={classes.innerItemImage}
                  src={getImageUrl(item.imageId ? item.imageId : null)}
                  alt=""
                />
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography className={classes.innerContentText}>
                    {item.name}
                  </Typography>
                  {item.productVariant && (
                    <Typography
                      className={`${classes.innerContentText} ${classes.textVariant}`}
                    >
                      {Object.values(item.productVariant).join('/')}
                    </Typography>
                  )}
                  <div>
                    <Typography className={classes.innerContentText}>
                      {`Cantidad: ${item.quantity}`}
                    </Typography>
                    <Typography
                      className={`${classes.innerContentText} ${classes.quantity}`}
                      style={{fontWeight: 500}}
                    >
                      {commaFormat(
                        parseFloat(item.price) * item.quantity,
                        currency?.symbol
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
              {item.comment ? (
                <Typography className={classes.itemComment}>
                  {item.comment}
                </Typography>
              ) : null}
              <Divider className={classes.divider} />
            </div>
          ))}
        <div className={classes.totalsRow}>
          <Typography className={classes.innerContentText}>Subtotal</Typography>
          <Typography className={classes.innerContentText}>
            {commaFormat(order.invoice?.detail?.products, currency?.symbol)}
          </Typography>
        </div>
        {order.invoice.detail.discount && (
          <div className={classes.totalsRow}>
            <Typography className={classes.labelDscto}>
              {order.invoice.coupon
                ? order.invoice.coupon
                : order.invoice.promotions
                ? order.invoice.promotions[0]
                : 'Descuento'}
            </Typography>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {/* <Typography className={classes.chipDscto}>
                {`${(
                  (Number(order.invoice.detail.discount) /
                    Number(order.invoice.detail.products)) *
                  100
                ).toFixed(0)}%`}
              </Typography> */}
              <Typography className={classes.labelDscto}>
                {`-${commaFormat(
                  Number(
                    order.invoice.detail.discount.total_discount
                      ? order.invoice.detail.discount.total_discount
                      : 0
                  ) * -1,
                  currency?.symbol
                )}`}
              </Typography>
            </div>
          </div>
        )}
        <div className={classes.totalsRow}>
          <Typography className={classes.innerContentText}>Envío</Typography>
          <Typography className={classes.innerContentText}>
            {`${
              order.invoice.detail.last_mile_shipping
                ? commaFormat(
                    order.invoice.detail.last_mile_shipping,
                    currency?.symbol
                  )
                : '--'
            }`}
          </Typography>
        </div>
        <div className={classes.totalsRow}>
          <Typography className={`${classes.innerContentText} ${classes.bold}`}>
            Total
          </Typography>
          <Typography className={`${classes.innerContentText} ${classes.bold}`}>
            {`${
              order.invoice.detail.total &&
              (order.invoice.detail.last_mile_shipping ||
                order.deliveryMethod === 'PICK_UP')
                ? commaFormat(order.invoice.detail.total, currency?.symbol)
                : 'Por confirmar'
            }`}
          </Typography>
        </div>
      </>
    );
  }, [classes, currency, order]);

  return (
    <Collapsible
      startClosed={true}
      paddingH={1.5}
      title={
        <div style={{width: '100%'}}>
          <Grid container>
            <Grid item xs={7}>
              <div className={classes.wrapHeader}>
                <Typography className={classes.heading}>
                  Número de orden
                </Typography>
                <div className={classes.wrapState}>
                  <CustomButton
                    to={`/orders/${order.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography className={classes.subHeading}>
                      {order.id}
                    </Typography>
                  </CustomButton>
                  <Hidden smDown>{orderStatePill}</Hidden>
                </div>
              </div>
            </Grid>
            <Grid item xs={5}>
              <div className={classes.wrapHeader}>
                <Typography className={classes.heading}>Fecha</Typography>
                <Typography className={classes.subHeading}>
                  {parsedDate}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>
      }
    >
      {orderItems}
    </Collapsible>
  );
}

CollapseItem.propTypes = {
  order: PropTypes.object,
};
